/**
 * ELF-CIRCLE services rail — section `#5b`, "Slide through the roster".
 *
 * Four service panels share one row — a flat colour each while closed, their
 * photo once open. The hovered (or focused, or tapped)
 * panel expands to ~5.2x the width of its siblings, so four services occupy
 * the vertical space of one. Expansion is pure CSS `flex-grow`; this component
 * only tracks which panel is open.
 *
 * Both the imagery and every piece of copy here are admin-editable — the
 * values below are the defaults shown until someone overrides them.
 */

import React, { useState } from 'react';
import EditableImage from './EditableImage';
import EditableText from './EditableText';
import type { LandingSlot } from './landingImages';
import { useConsult } from './elfConsult';
import { useInViewVideo } from './useInViewVideo';
import backdropPoster from '../../../../assets/elf/services-colourblast-poster.jpg';
import backdropVideo from '../../../../assets/elf/services-colourblast.mp4';

interface Service {
  /** Content-key prefix for this panel, e.g. `service.marketing`. */
  key: string;
  name: string;
  kicker: string;
  slot: LandingSlot;
  points: string[];
  cta: string;
  price: string;
}

const SERVICES: Service[] = [
  {
    key: 'service.marketing',
    name: 'Marketing',
    kicker: 'Brand · Campaigns · Talent',
    slot: 'service.marketing',
    points: [
      'Campaign strategy & rollout',
      'Social, content & creator seeding',
      'Festival & runway activations',
    ],
    cta: 'Book Marketing',
    price: 'from $—',
  },
  {
    key: 'service.web',
    name: 'Web Development',
    kicker: 'VOILACODE · Build · Scale',
    slot: 'service.web',
    points: [
      'Site, booking & ticketing build',
      'E-commerce, checkout & payments',
      'Scheduler, chatbot & integrations',
    ],
    cta: 'Book Web Dev',
    price: 'from $—',
  },
  {
    key: 'service.events',
    name: 'Events & Festivals',
    kicker: 'Production · Ticketing',
    slot: 'service.events',
    points: [
      'Run of show & production',
      'Registration & guest lists',
      'On-site capture & recap',
    ],
    cta: 'Book Events',
    price: 'from $—',
  },
  {
    key: 'service.merch',
    name: 'Merch & Commerce',
    kicker: 'Drops · Fulfilment',
    slot: 'service.merch',
    points: [
      'Capsule design & sampling',
      'Storefront & drop mechanics',
      'Secure checkout & fulfilment',
    ],
    cta: 'Book Merch',
    price: 'from $—',
  },
];

/** Maps each rail panel onto the dialog's interest picker. */
const SERVICE_INTEREST: Record<string, string> = {
  'service.marketing': 'Marketing',
  'service.web': 'Web Development',
  'service.events': 'Events & Festivals',
  'service.merch': 'Merch & Commerce',
};

/** Web Development opens by default, as in the design. */
const DEFAULT_OPEN = 1;

const SECTION = {
  eyebrow: 'Book a service',
  title: 'Slide through the roster',
  lede:
    'A single cinematic rail in the hero’s own language — panels open as the cursor moves, so four services take the vertical space of one.',
  metaLabel: 'Every path ends at',
  metaValue: 'Email / mobile → scheduler',
  note:
    'Hover or tab a panel to open it. Every Book button routes to the embedded scheduler after email or mobile capture.',
};

export const ElfServices: React.FC = () => {
  const consult = useConsult();
  const [open, setOpen] = useState(DEFAULT_OPEN);
  const backdropRef = useInViewVideo();

  return (
    <section id="services" className="elf-band elf-services">
      {/* Decorative loop behind the rail. Muted and inline, or phones refuse
          to autoplay it; the poster stands in until it starts, or if it can't. */}
      <video
        ref={backdropRef}
        className="elf-services__bg"
        src={backdropVideo}
        poster={backdropPoster}
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="elf-services__scrim" />
      <div className="sigrule elf-band__rule" />
      <div className="elf-band__inner">
        <div className="elf-section__head">
          <div style={{ maxWidth: 640 }}>
            <span className="eyebrow an" style={{ animationDelay: '.05s' }}>
              <i />
              <EditableText contentKey="services.eyebrow" fallback={SECTION.eyebrow} label="Eyebrow" />
            </span>
            <EditableText
              as="h2"
              contentKey="services.title"
              fallback={SECTION.title}
              label="Section title"
              className="an elf-section__title"
              style={{ animationDelay: '.12s' }}
            />
            <EditableText
              as="p"
              contentKey="services.lede"
              fallback={SECTION.lede}
              label="Section intro"
              multiline
              className="an elf-section__lede"
              style={{ animationDelay: '.18s' }}
            />
          </div>
          <div className="an elf-rail__meta" style={{ animationDelay: '.24s' }}>
            <EditableText contentKey="services.meta.label" fallback={SECTION.metaLabel} label="Meta label" />
            <EditableText contentKey="services.meta.value" fallback={SECTION.metaValue} label="Meta value" />
          </div>
        </div>

        <div className="elf-rail">
          {SERVICES.map((service, i) => (
            /* A div rather than a <button>: in admin edit mode this panel
               contains the image-replace button, and a button inside a
               button is invalid — the outer one swallows the click. */
            <div
              key={service.key}
              role="button"
              tabIndex={0}
              className={i === open ? 'railtile on' : 'railtile'}
              aria-pressed={i === open}
              aria-label={service.name}
              onPointerEnter={(e) => {
                // Mouse only: on touch this fires as a scroll begins; a tap arrives as the click.
                if (e.pointerType === 'mouse') setOpen(i);
              }}
              onFocus={() => setOpen(i)}
              onClick={() => setOpen(i)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen(i);
                }
              }}
            >
              <EditableImage slot={service.slot} alt="" />
              <span className="railscrim" />
              <span className="railvert">
                <EditableText contentKey={`${service.key}.name`} fallback={service.name} label="Service name" />
              </span>
              <span className="railbody">
                <EditableText
                  contentKey={`${service.key}.kicker`}
                  fallback={service.kicker}
                  label="Kicker"
                  className="elf-rail__kicker"
                />
                <EditableText
                  contentKey={`${service.key}.name`}
                  fallback={service.name}
                  label="Service name"
                  className="elf-rail__name"
                />
                <EditableText
                  contentKey={`${service.key}.points`}
                  fallback={service.points.join('\n')}
                  label="Bullet points"
                  multiline
                  className="elf-rail__points"
                >
                  {(value) =>
                    value
                      .split('\n')
                      .map((point) => point.trim())
                      .filter(Boolean)
                      .map((point) => (
                        <span key={point}>
                          <b>—</b>
                          {point}
                        </span>
                      ))
                  }
                </EditableText>
                <span className="elf-rail__foot">
                  <button
                    type="button"
                    className="pillcta"
                    onClick={(e) => {
                      e.stopPropagation();
                      consult?.open({
                        source: service.key,
                        interest: SERVICE_INTEREST[service.key],
                        title: service.cta,
                      });
                    }}
                  >
                    <EditableText
                      contentKey={`${service.key}.cta`}
                      fallback={service.cta}
                      label="Button label"
                    />{' '}
                    <span>→</span>
                  </button>
                  <EditableText
                    contentKey={`${service.key}.price`}
                    fallback={service.price}
                    label="Price line"
                    className="elf-rail__price"
                  />
                </span>
              </span>
            </div>
          ))}
        </div>

        {/* Footnote is fixed copy — it explains the interaction, not the offer. */}
        <p className="an elf-section__note" style={{ animationDelay: '.5s' }}>
          {SECTION.note}
        </p>
      </div>
    </section>
  );
};

export default ElfServices;
